import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';

export default function VisualSearchTab() {
  const { user, userProfile, refreshUserProfile } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [searching, setSearching] = useState(false);
  const [modelTier, setModelTier] = useState('clip');
  const [matchThreshold, setMatchThreshold] = useState(0.25);
  const [stripBackground, setStripBackground] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [keyword, setKeyword] = useState('');
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const getMLBaseUrl = () => {
    return import.meta.env.VITE_ML_API_URL || 'http://localhost:8001/api/v1';
  };

  // Fetch active categories on mount for the scope filtering dropdown
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error: catError } = await supabase
          .from('categories')
          .select('id, name')
          .order('name');
        if (!catError && data) {
          setCategories(data);
        }
      } catch (err) {
        console.error('Failed to load categories for visual search scoped dropdown:', err);
      }
    };
    fetchCategories();
  }, []);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    if (!selected.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }

    setFile(selected);
    setPreview(URL.createObjectURL(selected));
    setUploadedUrl(null);
    setMatches([]);
    setError(null);
    setSuccess(null);
  };

  // Symmetrical client-side Canvas thresholding filter to generate solid binary silhouettes
  const generateSilhouetteBlob = (imageFile) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(imageFile);
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imgData.data;
        
        // High-contrast binary silhouette conversion:
        // Turns background black, and any active part contours solid white.
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i+1];
          const b = data[i+2];
          const brightness = (r + g + b) / 3;
          
          if (brightness < 45) { // Dark background threshold
            data[i] = 0;     // R
            data[i+1] = 0;   // G
            data[i+2] = 0;   // B
          } else {
            data[i] = 255;   // R
            data[i+1] = 255; // G
            data[i+2] = 255; // B
          }
        }
        ctx.putImageData(imgData, 0, 0);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(img.src);
          // Create a new File from the blob to match Supabase requirements
          const processedFile = new File([blob], 'processed_silhouette.jpg', { type: 'image/jpeg' });
          resolve(processedFile);
        }, 'image/jpeg', 0.9);
      };
      img.onerror = (err) => {
        URL.revokeObjectURL(img.src);
        reject(err);
      };
    });
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      // Symmetrical Self-Healing Storage Bucket Check
      try {
        const { data: buckets, error: listError } = await supabase.storage.listBuckets();
        if (!listError) {
          const hasBucket = buckets.some(b => b.name === 'ai-training-data');
          if (!hasBucket) {
            await supabase.storage.createBucket('ai-training-data', {
              public: true
            });
          }
        }
      } catch (bucketErr) {
        console.warn('⚠️ Warning: Failed self-healing bucket check:', bucketErr);
      }

      // 1. Process Silhouette if background stripping is enabled
      let fileToUpload = file;
      if (stripBackground) {
        setSuccess('Processing high-contrast geometric silhouette filter...');
        fileToUpload = await generateSilhouetteBlob(file);
        // Temporarily set preview to show the filtered silhouette
        setPreview(URL.createObjectURL(fileToUpload));
      }

      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id || 'guest';
      const fileExt = fileToUpload.name.split('.').pop() || 'jpg';
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const uploadPath = `user-queries/${userId}/${fileName}`;

      const { error: uploadErr } = await supabase.storage
        .from('ai-training-data')
        .upload(uploadPath, fileToUpload, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadErr) throw uploadErr;

      const publicUrl = supabase.storage.from('ai-training-data').getPublicUrl(uploadPath).data.publicUrl;
      setUploadedUrl(publicUrl);

      // Update persistent search history in raw_user_meta_data (up to 10)
      if (user?.id) {
        const currentHistory = userProfile?.raw_user_meta_data?.visual_search_history || [];
        const newItem = {
          id: Math.random().toString(36).substring(2),
          url: publicUrl,
          created_at: new Date().toISOString()
        };
        const updatedHistory = [newItem, ...currentHistory].slice(0, 10);
        
        await supabase
          .from('users')
          .update({
            raw_user_meta_data: {
              ...userProfile?.raw_user_meta_data,
              visual_search_history: updatedHistory
            }
          })
          .eq('id', user.id);
        
        if (refreshUserProfile) {
          await refreshUserProfile();
        }
      }

      setSuccess('Photo uploaded successfully! Ready to run visual parts search.');
    } catch (err) {
      console.error('Error uploading search query:', err);
      setError('Failed to upload photo to storage. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSearch = async () => {
    if (!uploadedUrl) return;
    setSearching(true);
    setError(null);
    setMatches([]);

    try {
      const response = await fetch(`${getMLBaseUrl()}/ml/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_url: uploadedUrl,
          model_tier: modelTier,
          match_threshold: Number(matchThreshold),
          match_count: 6,
          category_id: selectedCategory || null,
          keyword: keyword.trim() || null
        })
      });

      if (!response.ok) throw new Error('Search failed on ML server');
      const data = await response.json();
      if (!data.success) throw new Error(data.message);

      setMatches(data.matches);
      if (data.matches.length === 0) {
        setError('No similar parts found in the catalog matching your filters. Try uploading a clearer shot, lowering the threshold, or clearing the scope.');
      } else {
        setSuccess(`Found ${data.matches.length} matching catalog component(s)!`);
      }
    } catch (err) {
      console.error('Visual search query error:', err);
      setError('Failed to query similarity. Ensure the dedicated ML microservice is active.');
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setFile(null);
    setPreview(null);
    setUploadedUrl(null);
    setMatches([]);
    setSelectedCategory('');
    setKeyword('');
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-soft space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="Camera" className="text-primary" size={24} />
          Visual Parts Sourcing (Experimental)
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Upload a photo of a physical part, and our deep learning model will map its geometric features to identify the matching 3D component.
        </p>
      </div>

      {error && (
        <div className="bg-destructive/10 border-l-4 border-destructive p-4 rounded text-sm text-destructive flex items-center gap-2">
          <Icon name="AlertTriangle" size={18} />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-primary/10 border-l-4 border-primary p-4 rounded text-sm text-primary flex items-center gap-2">
          <Icon name="CheckCircle" size={18} />
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Upload & Parameters Container */}
        <div className="border border-dashed border-border rounded-xl p-6 flex flex-col justify-between relative bg-muted/20">
          <div className="flex-grow flex flex-col items-center justify-center">
            {preview ? (
              <div className="w-full space-y-4">
                <img src={preview} alt="Query Preview" className="max-h-64 object-contain mx-auto rounded-lg shadow-sm border border-border" />
                <div className="flex gap-3 justify-center">
                  {!uploadedUrl ? (
                    <Button onClick={handleUpload} disabled={uploading} className="w-40">
                      {uploading ? (
                        <span className="flex items-center gap-1.5 justify-center">
                          <Icon name="Loader" className="animate-spin" size={16} />
                          Uploading...
                        </span>
                      ) : 'Upload Photo'}
                    </Button>
                  ) : (
                    <div className="flex flex-col w-full gap-4 max-w-sm mx-auto">
                      {/* Model Tier Selector */}
                      <div className="flex flex-col text-left gap-1.5">
                        <label className="text-xs font-semibold text-muted-foreground uppercase">
                          Select AI Search Engine
                        </label>
                        <select 
                          value={modelTier} 
                          onChange={(e) => setModelTier(e.target.value)}
                          className="p-2 border rounded-lg bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 outline-none"
                        >
                          <option value="clip">CLIP ViT (General visual features)</option>
                          <option value="dino">DINOv2 (Geometric CAD shapes)</option>
                        </select>
                      </div>

                      {/* Threshold Slider Selector */}
                      <div className="flex flex-col text-left gap-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-semibold text-muted-foreground uppercase">
                            Similarity Match Threshold
                          </label>
                          <span className="font-mono text-xs font-bold text-primary">{Math.round(matchThreshold * 100)}%</span>
                        </div>
                        <input
                          type="range"
                          min="0.05"
                          max="0.95"
                          step="0.05"
                          value={matchThreshold}
                          onChange={(e) => setMatchThreshold(Number(e.target.value))}
                          className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary focus:outline-none"
                        />
                      </div>

                      {/* Scope Filter: Categories Dropdown */}
                      <div className="flex flex-col text-left gap-1.5">
                        <label className="text-xs font-semibold text-muted-foreground uppercase">
                          Limit Scope: Category
                        </label>
                        <select 
                          value={selectedCategory} 
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="p-2 border rounded-lg bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 outline-none"
                        >
                          <option value="">All Categories (Global search)</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Scope Filter: Keyword Input */}
                      <div className="flex flex-col text-left gap-1.5">
                        <label className="text-xs font-semibold text-muted-foreground uppercase">
                          Limit Scope: Keyword
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., rim, caliper, gear"
                          value={keyword}
                          onChange={(e) => setKeyword(e.target.value)}
                          className="p-2 border rounded-lg bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 outline-none placeholder:text-muted-foreground"
                        />
                      </div>

                      <Button onClick={handleSearch} disabled={searching} className="w-full mt-2">
                        {searching ? (
                          <span className="flex items-center gap-1.5 justify-center">
                            <Icon name="Loader" className="animate-spin" size={16} />
                            Matching Shapes...
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 justify-center">
                            <Icon name="Search" size={16} />
                            Search Similar Parts
                          </span>
                        )}
                      </Button>
                    </div>
                  )}
                  <Button variant="outline" onClick={clearSearch} className="px-3">
                    <Icon name="Trash2" size={16} />
                  </Button>
                </div>
              </div>
            ) : (
              <label className="cursor-pointer flex flex-col items-center py-12 w-full">
                <Icon name="UploadCloud" className="text-muted-foreground mb-3" size={48} />
                <span className="text-sm font-semibold text-foreground">Click to select photo</span>
                <span className="text-xs text-muted-foreground mt-1">PNG, JPG, or WEBP up to 10MB</span>
                <input type="file" onChange={handleFileChange} className="hidden" accept="image/*" />
              </label>
            )}
          </div>

          {/* Toggle for Background Stripping */}
          {!uploadedUrl && file && (
            <div className="mt-4 p-3 bg-card border border-border rounded-lg flex items-center justify-between text-left max-w-sm mx-auto w-full shadow-soft">
              <div>
                <label htmlFor="strip-background" className="text-xs font-bold text-foreground block uppercase">
                  Strip Background (Silhouette)
                </label>
                <span className="text-[10px] text-muted-foreground block leading-tight">
                  Convert image into flat black-&-white silhouette to isolate geometry.
                </span>
              </div>
              <input
                id="strip-background"
                type="checkbox"
                checked={stripBackground}
                onChange={(e) => setStripBackground(e.target.checked)}
                className="w-4 h-4 text-primary rounded focus:ring-primary cursor-pointer"
              />
            </div>
          )}

          {/* Uploaded History Thumbnail Track */}
          {userProfile?.raw_user_meta_data?.visual_search_history?.length > 0 && (
            <div className="border-t border-border mt-6 pt-4 w-full text-left">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                Your Uploaded Photos (Max 10)
              </h3>
              <div className="flex gap-2 overflow-x-auto pb-2 pr-1 scrollbar-thin">
                {userProfile.raw_user_meta_data.visual_search_history.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setPreview(item.url);
                      setUploadedUrl(item.url);
                      setMatches([]);
                      setError(null);
                      setSuccess('Loaded past photo from history! Click search to match parts.');
                    }}
                    className={`w-12 h-12 rounded-lg border-2 overflow-hidden flex-shrink-0 relative transition-all hover:scale-105 ${
                      uploadedUrl === item.url ? 'border-primary shadow-sm scale-105 opacity-100' : 'border-border opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={item.url} alt="Past Query" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Results Container */}
        <div className="bg-muted/10 border border-border rounded-xl p-6 flex flex-col justify-between">
          <div className="flex-grow">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              AI Vector Matches
            </h3>
            
            {searching ? (
              <div className="flex flex-col items-center justify-center text-center py-20 text-primary">
                <Icon name="Loader" className="animate-spin mb-4 text-primary" size={40} />
                <p className="text-sm font-semibold">AI Sourcing in Progress</p>
                <p className="text-xs text-muted-foreground mt-1 px-4 leading-relaxed">
                  Downloading user photo, extracting deep geometric visual features, and running Cosine Similarity search over pgvector...
                </p>
              </div>
            ) : matches.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 max-h-[380px] overflow-y-auto pr-1">
                {matches.map((item, idx) => (
                  <div key={idx} className="bg-card border border-border rounded-lg p-3 hover:shadow-soft transition-all flex flex-col justify-between">
                    <div>
                      <img src={item.image_url} alt={item.component_name} className="w-full h-24 object-contain rounded bg-muted/20 border" />
                      <h4 className="text-xs font-semibold text-foreground mt-2 truncate" title={item.component_name}>
                        {item.component_name}
                      </h4>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[10px] bg-primary/10 text-primary font-bold px-1.5 py-0.5 rounded">
                          {item.similarity}% match
                        </span>
                      </div>
                    </div>
                    <a
                      href={`/3d-model-viewer?id=${item.product_id}&componentId=${item.component_id || ''}&componentName=${item.component_name}&viewMode=x-ray&isPlaying=false`}
                      className="mt-3 block text-center bg-primary text-primary-foreground text-[10px] font-bold py-1.5 rounded hover:opacity-90 transition-opacity"
                    >
                      View in 3D Viewer
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center py-20 text-muted-foreground">
                <Icon name="Cpu" className="mb-2 text-muted-foreground/50" size={32} />
                <p className="text-sm font-medium">No active search queries</p>
                <p className="text-xs mt-1">Upload a part photo and click search to view similarity embeddings matches.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
